import React, {
    useEffect, useMemo, useRef, useState,
} from 'react';
import * as yup from 'yup';
import moment from 'moment';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useYupValidationResolver } from 'hooks/useYupValidationResolver';

import { Button, Icon } from 'ui-library';

import { ICON_TYPE_DELETE } from 'ui-library/components/Icon';
import { getValidationMessage } from 'utils/validation';
import { SYSTEM_MESSAGE_PLATFORMS } from 'constants/constants';
import SystemMessagePreview from 'components/SystemMessagePreview';
import {
    getSystemMessageEndDateFormat,
    getSystemMessageStartDateFormat,
} from 'utils/datetime';
import { useGenericAPIMutation } from 'hooks/mutations/useGenericAPIMutation';
import Error from 'components/AtomicStructure/atoms/Error';
import { LangSource } from 'translations/adaptors/languagesAdapt';
import {
    adaptCreateSystemMessagePayload,
    adaptFile,
    getPlatformOptions,
} from './utils';
import SystemMessageFormFields from './SystemMessageFormFields';
import {
    FormValues,
    Language,
    Platform,
    TranslationFields,
    IFormType,
} from './SystemMessage.types';

// defaults
const defaultTranslations: Record<Language, TranslationFields> = {
    de: {
        title: '', body: '', link: '', document: null,
    },
    fr: {
        title: '', body: '', link: '', document: null,
    },
    it: {
        title: '', body: '', link: '', document: null,
    },
    en: {
        title: '', body: '', link: '', document: null,
    },
};

// form
export default function SystemMessageForm({
    languages,
    activeLang,
    setCurrentLang,
    onCloseModal,
    messageData = null,
    onCreateMessageSuccess,
    formType = undefined,
}: {
    languages: LangSource[];
    activeLang: Language;
    setCurrentLang: (lang: Language) => void;
    onCloseModal: () => void;
    messageData?:
        | (FormValues & { id: number; uId: string; startDateDisabled: boolean })
        | null;
    onCreateMessageSuccess?: () => void;
    formType?: IFormType;
}) {
    const {
        t,
        i18n: { language },
    } = useTranslation();

    const { mandatoryError } = getValidationMessage();
    const topRef = useRef<HTMLFormElement>(null);

    const [showPreview, setShowPreview] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [timeRangeError, setTimeRangeError] = useState('');
    const [createdMessageUID, setCreatedMessageUID] = useState<string | null>(null);
    const [uploadedLangs, setUploadedLangs] = useState<string[]>([]);

    // schema
    const translationSchema = useMemo(
        () => yup.object({
            title: yup.string().required(mandatoryError).max(140),

            body: yup.string().required(mandatoryError).max(1000),

            link: yup
                .string()
                .nullable()
                .transform((v) => (v === '' ? null : v))
                .url('Invalid URL')
                .test('link-required-if-any', mandatoryError, function (value) {
                    // eslint-disable-next-line react/no-this-in-sfc
                    const allTranslations = this.from?.[1]?.value;

                    if (!allTranslations) return true;
                    const anyFilled = Object.values(allTranslations).some(
                        (translation: any) => !!translation?.link,
                    );

                    return !anyFilled || !!value;
                }),

            document: yup
                .mixed<File>()
                .nullable()
                .transform((v) => (v === '' ? null : v))
                .test('doc-required-if-any', mandatoryError, function (value) {
                    // eslint-disable-next-line react/no-this-in-sfc
                    const allTranslations = this.from?.[1]?.value;

                    if (!allTranslations) return true;
                    const anyFilled = Object.values(allTranslations).some(
                        (translation: any) => !!translation?.document,
                    );

                    return !anyFilled || !!value;
                }),
        }),
        [mandatoryError],
    );

    const PLATFORM_OPTIONS = useMemo(() => getPlatformOptions(t), [language]);

    const languageOrder = languages.map((l) => l.value);

    const PLATFORMS = useMemo(
        () => PLATFORM_OPTIONS?.map((platform) => platform.value),
        [PLATFORM_OPTIONS],
    );

    const schema: yup.SchemaOf<FormValues> = useMemo(
        () => yup.object({
            translations: yup.object({
                de: translationSchema,
                fr: translationSchema,
                it: translationSchema,
                en: translationSchema,
            }),
            startDate: yup.string().required(mandatoryError),
            endDate: yup.string().required(mandatoryError),
            platform: yup.mixed<Platform>().oneOf(PLATFORMS).required(),
        }),
        [translationSchema, PLATFORMS],
    );

    const {
        control,
        watch,
        handleSubmit,
        setError,
        setValue,
        formState: { errors },
        reset,
    } = useForm<FormValues>({
        resolver: useYupValidationResolver(schema),
        defaultValues: messageData || {
            translations: defaultTranslations,
            startDate: moment(),
            platform: SYSTEM_MESSAGE_PLATFORMS.MobileAdvisor as Platform,
        },
    });

    const {
        startDate: startDateValue,
        endDate: endDateValue,
        platform: platformValue,
        translations,
    } = watch();

    const {
        title, body, link, document,
    } = translations?.[activeLang] || {};

    const firstErrorLang = useMemo(() => {
        const firstErrorPane = Object.keys(errors)
            .sort((a, b) => {
                const langA = a.split('.')[1];
                const langB = b.split('.')[1];

                return languageOrder.indexOf(langA) - languageOrder.indexOf(langB);
            })
            .find((err) => err?.includes('translations'));

        return firstErrorPane?.split('.')?.[1] as Language | undefined;
    }, [errors]);

    useEffect(() => {
        if (firstErrorLang) {
            setCurrentLang(firstErrorLang);
        }
    }, [firstErrorLang]);

    const handleCloseModal = () => {
        if (showPreview) {
            setShowPreview(false);
        } else {
            onCloseModal();
        }
    };

    const { mutateAsync: handleMutation, isPending: isMutationLoading } = useGenericAPIMutation();

    const onSubmit = async (data: FormValues) => {
        setErrorMessage('');
        topRef.current?.scrollIntoView({ behavior: 'smooth' });

        if (!showPreview) {
            // Check Time Range
            const timeRange = {
                startDateTime: messageData?.startDateDisabled
                    ? moment
                        .utc(messageData.startDate)
                        .format('YYYY-MM-DDTHH:mm:ss[Z]')
                    : getSystemMessageStartDateFormat({ date: data.startDate }),
                endDateTime: getSystemMessageEndDateFormat({
                    date: data?.endDate,
                }),
                platform: +data.platform,
                idToSkip:
                    formType === 'edit-message' ? messageData?.id || 0 : 0,
            };

            const checkRangeResponse: any = await handleMutation({
                service: 'customSystemMessageService',
                API: 'checkSystemMessageTimeRange',
                payload: timeRange,
            });

            if (
                checkRangeResponse?.status !== 200
                || checkRangeResponse?.data?.existsOverlappingTimeRange
            ) {
                setTimeRangeError(
                    t('SystemMessage.Form.Error.MessageExistInTimeFrame'),
                );

                return;
            }

            setTimeRangeError('');
            setShowPreview(true);
            setCurrentLang('de');

            return;
        }

        if (formType === 'edit-message' && messageData) {
            try {
                const updateResponse: any = await handleMutation({
                    service: 'customSystemMessageService',
                    API: 'updateSystemMessage',
                    payload: {
                        messageId: messageData?.id,
                        ...adaptCreateSystemMessagePayload({
                            data,
                            oldMessageData: messageData,
                        }),
                    },
                    invalidateQueryKeys: ['getData_getSystemMessages', 'getData_getActiveSystemMessageDetails'],
                });

                if (updateResponse?.status !== 200) {
                    setErrorMessage(t('general.somethingWentWrong'));

                    return;
                }

                // Handle Document Changes (delete old and upload new if changed)
                const translationKeys = Object.keys(data?.translations || {});

                // eslint-disable-next-line no-restricted-syntax
                for (const key of translationKeys) {
                    const newFile = data?.translations[key]?.document;
                    const oldDocument = messageData?.translations?.[key]?.document;
                    const oldDocumentId = messageData?.translations?.[key]?.documentId;

                    // If document was changed (new file exists and is different from old)
                    if (newFile !== oldDocument) {
                        if (oldDocumentId) {
                            // eslint-disable-next-line no-await-in-loop
                            await handleMutation({
                                service: 'documents',
                                API: 'deleteDocument',
                                payload: { documentId: oldDocumentId },
                            });
                        }
                    }

                    if (newFile && newFile !== oldDocument) {
                        // Upload new document
                        // eslint-disable-next-line no-await-in-loop
                        const adaptedFile = await adaptFile(newFile);
                        // eslint-disable-next-line no-await-in-loop
                        const uploadResponse = await handleMutation({
                            service: 'documents',
                            API: 'postDocument',
                            payload: {
                                filename: adaptedFile.fileName,
                                file: adaptedFile.file,
                                contractId: messageData?.uId,
                                metadataReferenceId:
                                    'ExtSystemMessageAttachment',
                                languageTwoLetterIsoCode: key,
                            },
                        });

                        if (!uploadResponse?.data) {
                            setErrorMessage(
                                t('general.somethingWentWrong'),
                            );

                            return;
                        }
                    }
                }

                // Success
                setShowPreview(false);
                onCreateMessageSuccess?.();
                reset();
            } catch (error: any) {
                setErrorMessage(t('general.somethingWentWrong'));
            }

            return;
        }

        try {
            // Create message if not already created this session
            let contractUID = createdMessageUID;

            if (!contractUID) {
                const createResponse: any = await handleMutation({
                    service: 'customSystemMessageService',
                    API: 'createSystemMessage',
                    payload: adaptCreateSystemMessagePayload({ data }),
                    invalidateQueryKeys: ['getData_getSystemMessages', 'getData_getActiveSystemMessageDetails'],
                });

                contractUID = createResponse?.data?.uid;
                if (!contractUID) {
                    setErrorMessage(t('general.somethingWentWrong'));

                    return;
                }

                setCreatedMessageUID(contractUID);
                setUploadedLangs([]);
            }

            // Upload documents, skipping already-uploaded languages
            const translationKeys = Object.keys(data?.translations || {});
            const successfulLangs = [...uploadedLangs];

            // eslint-disable-next-line no-restricted-syntax
            for (const key of translationKeys) {
                const file = data?.translations[key]?.document;

                if (file && !successfulLangs.includes(key)) {
                    // eslint-disable-next-line no-await-in-loop
                    const adaptedFile = await adaptFile(file);
                    // eslint-disable-next-line no-await-in-loop
                    const uploadResponse = await handleMutation({
                        service: 'documents',
                        API: 'postDocument',
                        payload: {
                            filename: adaptedFile.fileName,
                            file: adaptedFile.file,
                            contractId: contractUID,
                            metadataReferenceId: 'ExtSystemMessageAttachment',
                            languageTwoLetterIsoCode: key,
                        },
                    });

                    if (uploadResponse?.data) {
                        successfulLangs.push(key);
                        setUploadedLangs([...successfulLangs]);
                    } else {
                        setErrorMessage(t('general.somethingWentWrong'));

                        return;
                    }
                }
            }

            // Success — reset component state
            setCreatedMessageUID(null);
            setUploadedLangs([]);
            setShowPreview(false);
            onCreateMessageSuccess?.();
            reset();
        } catch (error: any) {
            setErrorMessage(t('general.somethingWentWrong'));
        }
    };

    return (
        <form
            onSubmit={handleSubmit(onSubmit)}
            style={{ maxWidth: 720 }}
            className="system-message-form"
            ref={topRef}
        >
            {showPreview ? (
                <>
                    <SystemMessagePreview
                        title={title}
                        body={body}
                        link={link}
                        document={document}
                    />

                    <div className="system-message-field-seperator" />
                    <p className="system-message-info-title">
                        {t('SystemMessage.Form.Preview.Info.Title')}
                        :
                    </p>
                    <div className="system-message-info">
                        <strong>
                            {t('SystemMessage.Form.Preview.Info.Start')}
                            :
                        </strong>
                        {' '}
                        {startDateValue
                            ? moment(startDateValue, 'DD.MM.YYYY').format(
                                'DD.MM.YYYY',
                            )
                            : t('SystemMessage.Form.Preview.Info.Immediately')}
                    </div>
                    <div className="system-message-info">
                        <strong>
                            {t('SystemMessage.Form.Preview.Info.End')}
                            :
                        </strong>
                        {' '}
                        {endDateValue
                            ? moment(endDateValue, 'DD.MM.YYYY').format('DD.MM.YYYY')
                            : t('SystemMessage.Form.Preview.Info.NotSet')}
                    </div>
                    <div className="system-message-info">
                        <strong>
                            {t('SystemMessage.Form.Preview.Info.Platform')}
                            :
                        </strong>
                        {' '}
                        {
                            PLATFORM_OPTIONS?.find(
                                (platfrm) => platfrm.value === platformValue,
                            )?.label
                        }
                    </div>
                    <div className="system-message-field-seperator" />
                </>
            ) : (
                <SystemMessageFormFields
                    languages={languages}
                    activeLang={activeLang}
                    control={control}
                    setError={setError}
                    platformOptions={PLATFORM_OPTIONS}
                    startDateValue={startDateValue}
                    endDateValue={endDateValue}
                    setValue={setValue}
                    timeRangeError={timeRangeError}
                    startDateDisabled={
                        formType === 'edit-message'
                            ? messageData?.startDateDisabled
                            : false
                    }
                />
            )}

            {errorMessage && <Error className="mt-20">{errorMessage}</Error>}

            {/* ACTIONS */}
            <div className="system-message-fields--actions">
                {formType === 'edit-message' && (
                    <Button
                        type="link"
                        className="system-message-fields--actions--discard-btn"
                        onClick={() => reset({ ...messageData })}
                    >
                        <Icon size={18} type={ICON_TYPE_DELETE} />
                        {t('SystemMessage.Form.DiscardChanges')}
                    </Button>
                )}
                <Button
                    type="secondary"
                    onClick={handleCloseModal}
                    className="ml-auto"
                >
                    {showPreview
                        ? t('confirmation.back')
                        : t('clientDashboard.close')}
                </Button>
                <Button
                    type="primary"
                    htmltype="submit"
                    loading={isMutationLoading}
                    disabled={isMutationLoading}
                >
                    {showPreview
                        ? t('SystemMessage.Form.PublishMessage')
                        : t('SystemMessage.Form.ShowPreview')}
                </Button>
            </div>
        </form>
    );
}
